import { Status } from 'hooks/requests/fetchUtils';
import useApplicationBasedOverrides from 'hooks/useApplicationBasedOverrides';
import useFetchApi from 'hooks/useFetchApi';
import useQueryParameters from 'hooks/useQueryParameters';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useIovation from 'hooks/useIovation';
import { complement, isNil, pickBy, isEmpty } from 'ramda';
import {
  CreditApplication,
  lockFields,
  mergeApplication,
} from 'state/creditApplication';
import {
  EnhancedCheckout,
  mergeEnhancedCheckoutState,
} from 'state/enhancedCheckout';
import {
  CreditAccount,
  mergeInstantCredit,
  Rewards,
  setAccounts,
  setCurrentAccount,
} from 'state/instantCredit';
import { receiveExistingAccounts } from 'fmc-sdk';
import useHandleExistingAccount from './handleExistingAccount';
import useFmcHost from 'hooks/useFmcHost';
import { selectTenant } from 'state/tenant';
const PATH = '/api/enhanced_checkout';

export interface SearchParams {
  mobilePhone?: string;
  token?: string;
  carrier?: string;
  keyword?: string;
  shortCode?: string;
  ssn?: string;
  zip?: string;
  otpCode?: string;
}

export interface SearchResults {
  token: string | undefined;
  accountNumber: string | undefined;
  expirationDate: string | undefined;
  expirationType: string | undefined;
  expirationValue: number | undefined;
  securityCode: string | undefined;
  firstPurchaseLimit: number | undefined;
  creditLimit: number | undefined;
  cardType: string | undefined;
  authAllowed: number | undefined;
  hasExistingAccount: boolean | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  middleInitial: string | undefined;
  address1: string | undefined;
  address2: string | undefined;
  city: string | undefined;
  state: string | undefined;
  zip: string | undefined;
  ssn: string | undefined;
  phone: string | undefined;
  returnCode: string | undefined;
  virtualCardAccountNumber: string | undefined;
  rewards?: Rewards;
  existingAccounts: CreditAccount[];
}

type EnhancedCheckoutHook = () => [
  <T extends SearchParams = SearchParams>(params: T) => Promise<SearchResults>,
  Status,
  boolean,
];

export class EnhancedCheckoutError extends Error {}

const prefillValues = (results: SearchResults): Partial<CreditApplication> =>
  pickBy(complement(isNil), {
    firstName: results.firstName,
    lastName: results.lastName,
    address1: results.address1,
    address2: results.address2,
    city: results.city,
    state: results.state,
    zip: results.zip,
    ssn: results.ssn,
    mobilePhone: results.phone,
  });

const useEnhancedCheckout: EnhancedCheckoutHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const dispatch = useDispatch();
  const {
    mockECO,
    location,
    channel,
    subchannel,
    applicationSubType,
  } = useQueryParameters();
  const tenant = useSelector(selectTenant);
  const fmcDispatch = useFmcHost();
  const overrideAttributes = useApplicationBasedOverrides();
  const iovationBlackbox = useIovation();

  const handleExistingAccount = useHandleExistingAccount();

  const searchConsumer = useCallback(
    async <T extends SearchParams = SearchParams>({
      carrier,
      keyword,
      mobilePhone,
      shortCode,
      ssn,
      token,
      zip,
      otpCode,
    }: T): Promise<SearchResults> => {
      setError(false);

      const blackbox = await iovationBlackbox;

      const response = await fetch(PATH, {
        method: 'POST',
        body: {
          ...overrideAttributes,
          carrier: carrier,
          keyword: keyword,
          mobilePhone,
          shortCode,
          ssn: ssn,
          token: token,
          zip: zip,
          iovationBlackbox: blackbox,
          mockResponse: mockECO,
          location,
          channel,
          subchannel,
          applicationSubType,
          otpCode,
        },
      });

      if (!response.ok) {
        setError(true);
        dispatch(mergeEnhancedCheckoutState({ consumerSearchResult: 'error' }));
        return Promise.reject(new EnhancedCheckoutError());
      }

      // TODO: Currently just shoving all of the data into place. Should probably
      // be more discerning somehow. Revisit this.
      const searchResults: SearchResults = await response.json();

      const enhancedCheckoutResult: Partial<
        EnhancedCheckout
      > = searchResults.hasExistingAccount
        ? { consumerSearchResult: 'existingAccount' }
        : { consumerSearchResult: 'error' };

      dispatch(mergeEnhancedCheckoutState(enhancedCheckoutResult));
      dispatch(mergeApplication(prefillValues(searchResults)));
      dispatch(
        lockFields({
          ssn: Boolean(searchResults.ssn),
          mobilePhone: Boolean(mobilePhone || searchResults.phone),
        }),
      );

      if (searchResults.hasExistingAccount) {
        const existingAccounts = searchResults.existingAccounts;
        // Ensure all top level keys are used.
        dispatch(mergeInstantCredit(searchResults));
        if (!isEmpty(existingAccounts)) {
          dispatch(setAccounts(searchResults.existingAccounts));
          if (tenant.enabledFeatures.virtualLookupMultipleAccounts)
            fmcDispatch(
              receiveExistingAccounts(
                searchResults.existingAccounts.map(account => {
                  return {
                    message: 'Received existing account',
                    callId: account.callId || null,
                    rewards: account.rewards,
                    cardType: account.cardType,
                    ...account,
                  };
                }),
              ),
            );
        }

        if (searchResults.existingAccounts.length === 1) {
          handleExistingAccount(
            searchResults.existingAccounts[0],
            {
              sendToSdk: true,
              isValidated: true,
            },
            { mobilePhone: mobilePhone || null },
          );
          dispatch(setCurrentAccount(searchResults.existingAccounts[0]));
        }
      }

      return searchResults;
    },
    [
      iovationBlackbox,
      fetch,
      overrideAttributes,
      mockECO,
      location,
      channel,
      subchannel,
      applicationSubType,
      dispatch,
      tenant.enabledFeatures.virtualLookupMultipleAccounts,
      fmcDispatch,
      handleExistingAccount,
    ],
  );

  return [searchConsumer, status, error];
};

export default useEnhancedCheckout;
